import React, { useState, useEffect, useCallback } from 'react'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import { withStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import { CoreButton } from 'src/components/core'
import AddIcon from '@material-ui/icons/Add'
import { Icon } from '@iconify/react'
import { userLimitsUpdateCredits } from 'src/reducers/user-limits/action'
import { useSectionEdit } from './hooks/useSectionEdit'
import { useDragAndDrop } from './hooks/useDragAndDrop'
import { usePageManagement } from './hooks/usePageManagement'
import PageDetailsToggle from './components/PageDetailsToggle'
import Section from './components/Section'
import styles from './styles'
import {
  aICreatorUpdateOutlineAction,
  aICreatorClearOutlineAction,
} from 'src/reducers/ai-creator/action'
import { getCourseCreationProgressAction } from 'src/reducers/course/action'
import { track } from '@amplitude/analytics-browser'

const SectionEdit = ({
  classes,
  imageWillBeGenerated,
  setImageWillBeGenerated,
  onGenerateCourse,
  documentFollowing,
  setDocumentFollowing,
}) => {
  const dispatch = useDispatch()
  const data = useSelector(state => state.aICreator.outline)
  const attachments = useSelector(state => state.aICreator.attachments)

  // Get user type from Redux store for credit calculations
  const userLimits = useSelector(state => state.userLimits?.userLimits)
  const userType = userLimits?.[0]?.limit?.roleType || 1 // Default to free user (1)
  const currentCredits =
    useSelector(state => state.userLimits?.currentCredits) || 0

  // Get credit cost directly from API response
  const [creditCost, setCreditCost] = useState(data?.response?.credit_cost || 0)
  const [imageGenerationEnabled, setImageGenerationEnabled] = useState(
    imageWillBeGenerated || false,
  )

  // Sync local state with parent prop
  useEffect(() => {
    if (imageWillBeGenerated !== undefined) {
      setImageGenerationEnabled(imageWillBeGenerated)
    }
  }, [imageWillBeGenerated])

  // Sync parent state when local state changes
  const handleImageGenerationToggle = () => {
    const newValue = !imageGenerationEnabled
    setImageGenerationEnabled(newValue)
    if (setImageWillBeGenerated) {
      setImageWillBeGenerated(newValue)
    }
  }

  // Custom hooks
  const {
    // State
    expandedModules,
    sectionMenuAnchor,
    activeSectionIndex,
    popupStates,
    anchorElements,
    focusedGoalIndex,
    selectingSuggestion,
    popupLoadingStates,
    fieldSuggestions,
    sectionTitleSuggestions,
    pageTitleSuggestions,
    showAllPageDetails,
    isCourseHasDocument,

    // Refs
    sectionTitleRefs,
    moduleTitleRefs,
    goalRefs,
    modalRef,

    // Actions
    addSection,
    removeSection,
    duplicateSection,
    handleSectionTitleChange,
    addModule,
    removeModule,
    deletePage,
    handleModuleTitleChange,
    addGoal,
    addGoalWithText,
    removeGoal,
    removeLastGoal,
    handleGoalChange,
    toggleModule,
    handlePageDetailsToggle,
    handleSectionMenuOpen,
    handleSectionMenuClose,
    handleSectionAction,
    handleRegenerateSuggestions,
    handleGoalFocus,
    handleNewGoalFocus,
    handlePopupClose,
    handleSuggestionClick,
    handleGoalKeyDown,
    handleSectionTitleSuggestions,
    handlePageTitleSuggestions,
  } = useSectionEdit(
    data,
    dispatch,
    setCreditCost,
    documentFollowing,
    setDocumentFollowing,
  )

  const { onDragEnd } = useDragAndDrop(data, dispatch, setCreditCost)

  const {
    handleAddPageWithDocument,
    handleAddPage,
    getFileInfo,
  } = usePageManagement(data, dispatch, modalRef, setCreditCost)

  // Function to update outline on every change
  const updateOutlineOnChange = useCallback(
    newData => {
      // Update outline in backend if we have an outlineId

      if (data?.response?.outlineId) {
        dispatch(
          aICreatorUpdateOutlineAction(
            { outline: newData.response.outline },
            data.response.outlineId,
          ),
          response => {
            setCreditCost(response.credit_cost)
          },
        )
      }
    },
    [data?.response?.outlineId, dispatch],
  )

  // Override the setData function to automatically update outline
  const handleDataChange = useCallback(
    newData => {
      updateOutlineOnChange(newData)
    },
    [updateOutlineOnChange],
  )

  // Handle course generation with credits from API response
  const handleGenerateCourse = () => {
    onGenerateCourse(creditCost)
  }

  return (
    <div className={classes.root}>
      {(attachments.length === 0 ||
        (documentFollowing !== 'follow' && documentFollowing !== 'preserve')) && (
        <PageDetailsToggle
          classes={classes}
          showAllPageDetails={showAllPageDetails}
          onToggle={handlePageDetailsToggle}
        />
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sections" type="SECTION">
          {(provided, snapshot) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {data.response.outline.sections.map((section, sectionIndex) => (
                <Section
                  key={`section-${sectionIndex}`}
                  classes={classes}
                  section={section}
                  sectionIndex={sectionIndex}
                  expandedModules={expandedModules}
                  sectionTitleRefs={sectionTitleRefs}
                  moduleTitleRefs={moduleTitleRefs}
                  goalRefs={goalRefs}
                  popupStates={popupStates}
                  anchorElements={anchorElements}
                  fieldSuggestions={fieldSuggestions}
                  popupLoadingStates={popupLoadingStates}
                  sectionMenuAnchor={sectionMenuAnchor}
                  onSectionTitleChange={handleSectionTitleChange}
                  onSectionMenuOpen={handleSectionMenuOpen}
                  onSectionMenuClose={handleSectionMenuClose}
                  onSectionAction={handleSectionAction}
                  onSectionTitleSuggestions={handleSectionTitleSuggestions}
                  sectionTitleSuggestions={sectionTitleSuggestions}
                  onModuleTitleChange={handleModuleTitleChange}
                  onPageTitleSuggestions={handlePageTitleSuggestions}
                  pageTitleSuggestions={pageTitleSuggestions}
                  onToggleModule={toggleModule}
                  onAddGoal={addGoal}
                  onAddGoalWithText={addGoalWithText}
                  onRemoveGoal={removeGoal}
                  onRemoveLastGoal={removeLastGoal}
                  onGoalChange={handleGoalChange}
                  onGoalFocus={handleGoalFocus}
                  onNewGoalFocus={handleNewGoalFocus}
                  onRegenerateSuggestions={handleRegenerateSuggestions}
                  onPopupClose={handlePopupClose}
                  onSuggestionClick={handleSuggestionClick}
                  onGoalKeyDown={handleGoalKeyDown}
                  onAddPage={handleAddPageWithDocument}
                  onDeletePage={deletePage}
                  getFileInfo={getFileInfo}
                  documentFollowing={documentFollowing}
                  setDocumentFollowing={setDocumentFollowing}
                />
              ))}
              {provided.placeholder}

              <CoreButton
                size="small"
                onClick={addSection}
                fullWidth
                variant="text"
                color="primary"
                startIcon={<AddIcon fontSize="small" />}
                className={classes.addSectionButton}
              >
                Add a Section
              </CoreButton>
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div className={classes.generateCourseButton}>
        <CoreButton onClick={handleGenerateCourse} fullWidth size="small">
          Generate Course{' '}
          <Icon icon="mage:coin-a" style={{ margin: '0 4px' }} /> {creditCost}
        </CoreButton>
      </div>
    </div>
  )
}

export default withStyles(styles)(SectionEdit)
